/**
 * dexGridPhase — recover the dex grid's true VERTICAL PHASE from the dex-number
 * boxes Apple Vision already returns, so the cell lattice tracks a continuously
 * scrolling grid instead of assuming it is aligned.
 *
 * THE DEFECT THIS EXISTS FOR (labelled shiny pass, 2026-07-22). The cell lattice
 * is fixed: `dexGridRows` walks up from `GRID_BOTTOM` (0.79) by a fixed pitch,
 * and the device telemetry shows `gridBottom` was the bare constant on all 628
 * frames of both passes. The Pokédex, however, scrolls CONTINUOUSLY — a frame
 * can be perfectly still (it passes the scroll-stability gate) and still be
 * parked half a row off the lattice. When it is, a tile rect straddles two real
 * rows, and the sprite ROI for one cell contains another cell's sprite.
 *
 * That is what the ground truth showed: grid ROW 1 scored 47% precision against
 * 99.2% for rows 2-4, 17 of 18 false writes were row 1, and 7 of them arrived as
 * `strong-instant` at spriteState "caught", confidence 1.0. The pixel classifier
 * was not wrong — there really was a caught sprite in that ROI. It belonged to
 * the row above. No density threshold can separate a real sprite from a real
 * sprite, which is why five passes of tuning chroma / mass / bodyFrac / lumStd
 * never moved it.
 *
 * THE SIGNAL. PoGo prints a cell's dex number in one of exactly two places:
 * centred (uncaught, cell-y ~0.50) or pinned to the bottom (caught, cell-y
 * ~0.85) — the same two anchors `numberPositionFromBox` already keys off. So for
 * every cell whose number Vision read, "where the number sits inside its assumed
 * cell" is a direct measurement of the lattice error, and the error is COMMON to
 * every cell in the frame because one scroll position produces it.
 *
 * The estimator below finds the single vertical offset that best explains all of
 * the frame's number positions at once. It is deliberately a frame-level fit and
 * not a per-cell correction: a per-cell fit cannot tell "caught, shifted up" from
 * "uncaught, aligned" — those are the same picture — whereas a whole frame of
 * cells pins the phase, because a shift moves every number together while the
 * caught/uncaught mix does not.
 */

/**
 * Where a number sits inside its cell, as a fraction of cell height, in each of
 * the two states PoGo renders.
 *
 * MEASURED, not inferred (device screenshot IMG_3795, 1179x2556, shiny Galar).
 * Against the lattice `dexGridRows` produces, an uncaught cell's number centre
 * lands at cell-y ~0.47 and a caught cell's at ~0.77 — a gap of 0.30, not the
 * 0.35 the older 0.50/0.85 figures in pokedexOcr's `numberPositionFromBox` note
 * imply. Those were fitted for the center-vs-bottom SPLIT, where only the
 * midpoint matters and an 0.08 error in either anchor is invisible; here the
 * anchor positions themselves are the model, so the error showed up directly as
 * misfit. On the first device pass 211 of 222 frames came back ambiguous, with
 * measured positions spanning 0.41..0.90 rather than clustering on the anchors.
 */
export const PHASE_ANCHOR_CENTER = 0.47;
export const PHASE_ANCHOR_BOTTOM = 0.77;

/**
 * Largest offset the estimator will search, in cell heights. Beyond ±0.5 the
 * number would have fallen out of its own cell's number band and been read
 * against a different lattice row entirely — a row MIS-ASSIGNMENT, which this
 * module deliberately does not try to repair (it reports low confidence and lets
 * the caller withhold the frame instead of relabelling cells).
 */
export const MAX_PHASE_OFFSET = 0.5;

/** Search step, in cell heights. 0.005 ≈ 1.4px on a 281px tile — well under a glyph. */
const PHASE_STEP = 0.005;

/**
 * A residual within this distance of an anchor is treated as explained. Set from
 * the anchor gap (0.35): half the gap is where the two anchors become
 * equidistant, so 0.12 keeps a comfortable margin on either side while still
 * admitting normal glyph-box jitter.
 */
const ANCHOR_TOLERANCE = 0.12;

/**
 * How much better the best offset must fit than the best RIVAL offset (one that
 * is not a small perturbation of it) before the estimate is called unambiguous.
 * With two anchors 0.35 apart, an offset of Δ and one of Δ±0.35 explain a
 * single-state frame equally well — every cell uncaught, say. This is the guard
 * against silently "correcting" by a third of a row on such a frame.
 */
const AMBIGUITY_MARGIN = 0.15;

/** Offsets nearer than this to the winner are the same solution, not a rival. */
const RIVAL_SEPARATION = 0.1;

/** One cell's contribution: where its number sits inside its assumed cell. */
export interface GridPhaseSample {
  /** Lattice row the cell was drawn from, 0 = topmost. Diagnostics only. */
  row: number;
  /**
   * (number box centre y − cell top) / cell height, in frame-fraction terms.
   * ~0.5 when the grid is aligned and the cell is uncaught, ~0.85 when aligned
   * and caught. Values outside [0, 1] are dropped as unusable.
   */
  cellRelY: number;
}

export interface GridPhaseEstimate {
  /**
   * Vertical lattice error in CELL HEIGHTS, positive meaning the real grid sits
   * BELOW where the lattice assumed. Add `offsetCells * cellHeight` to a cell
   * rect's y to register it. 0 when there is nothing to correct or nothing to
   * go on.
   */
  offsetCells: number;
  /** Samples that survived the [0,1] sanity filter and drove the fit. */
  samples: number;
  /** Mean distance from an anchor after correction, in cell heights. Lower is better. */
  residual: number;
  /**
   * True when a rival offset explains the frame nearly as well — a single-state
   * frame, or too few samples. The caller MUST NOT apply an ambiguous offset;
   * `offsetCells` is forced to 0 in that case so a careless caller is safe.
   */
  ambiguous: boolean;
  /** Fraction of samples sitting within ANCHOR_TOLERANCE of an anchor after correction. */
  explained: number;
}

/** Distance from a corrected residual to whichever anchor is nearer. */
function anchorDistance(v: number): number {
  return Math.min(Math.abs(v - PHASE_ANCHOR_CENTER), Math.abs(v - PHASE_ANCHOR_BOTTOM));
}

/**
 * Total misfit of one candidate offset. Distances are CLAMPED at the tolerance
 * so a couple of genuinely odd cells (a misread box, a cell straddling the PiP)
 * cannot drag the fit — the estimator answers "how many cells does this offset
 * explain", not "how far is the worst one".
 */
function costOf(samples: readonly GridPhaseSample[], offset: number): number {
  let cost = 0;
  for (const s of samples) {
    cost += Math.min(anchorDistance(s.cellRelY - offset), ANCHOR_TOLERANCE);
  }
  return cost / samples.length;
}

/**
 * Estimate the frame's grid phase from its number-box positions.
 *
 * Pure and total: no I/O, no frame state, safe to call per frame and trivially
 * testable. Returns a zero, ambiguous estimate when there is nothing to go on,
 * so every caller can treat "no correction" and "cannot tell" identically.
 */
export function estimateGridPhase(
  rawSamples: readonly GridPhaseSample[],
  opts?: { minSamples?: number },
): GridPhaseEstimate {
  const minSamples = opts?.minSamples ?? 4;
  const samples = rawSamples.filter(
    (s) => Number.isFinite(s.cellRelY) && s.cellRelY >= 0 && s.cellRelY <= 1,
  );
  const none: GridPhaseEstimate = {
    offsetCells: 0,
    samples: samples.length,
    residual: 0,
    ambiguous: true,
    explained: 0,
  };
  if (samples.length < minSamples) return none;

  let best = { offset: 0, cost: Number.POSITIVE_INFINITY };
  const steps = Math.round((MAX_PHASE_OFFSET * 2) / PHASE_STEP);
  const candidates: { offset: number; cost: number }[] = [];
  for (let i = 0; i <= steps; i++) {
    const offset = -MAX_PHASE_OFFSET + i * PHASE_STEP;
    const cost = costOf(samples, offset);
    candidates.push({ offset, cost });
    // Ties go to the SMALLER correction: with no evidence to prefer one, moving
    // the lattice less is the conservative choice.
    if (
      cost < best.cost - 1e-9 ||
      (Math.abs(cost - best.cost) <= 1e-9 && Math.abs(offset) < Math.abs(best.offset))
    ) {
      best = { offset, cost };
    }
  }

  // Ambiguity: is there a genuinely different offset that fits nearly as well?
  let rival = Number.POSITIVE_INFINITY;
  for (const c of candidates) {
    if (Math.abs(c.offset - best.offset) < RIVAL_SEPARATION) continue;
    rival = Math.min(rival, c.cost);
  }
  // Costs are clamped distances, so express the margin relative to the clamp —
  // that keeps the test scale-free rather than tied to the anchor gap.
  const margin = (rival - best.cost) / ANCHOR_TOLERANCE;
  const ambiguous = !Number.isFinite(rival) || margin < AMBIGUITY_MARGIN;

  let explained = 0;
  for (const s of samples) {
    if (anchorDistance(s.cellRelY - best.offset) <= ANCHOR_TOLERANCE) explained += 1;
  }

  return {
    offsetCells: ambiguous ? 0 : best.offset,
    samples: samples.length,
    residual: Number(best.cost.toFixed(4)),
    ambiguous,
    explained: Number((explained / samples.length).toFixed(3)),
  };
}

/**
 * Convert a Vision number box into a phase sample.
 *
 * `box` is normalised RELATIVE TO THE NUMBER-BAND ROI (see `numberRoiFrac` and
 * the box-semantics note in pokedexOcr) — this maps it back into cell-relative
 * space, which is the only space the estimator reasons in. Returns null when the
 * geometry is unusable, so callers can `?? continue` without a guard of their own.
 */
export function phaseSampleFromBox(
  box: { y: number; h: number } | undefined,
  roi: { y: number; h: number },
  cell: { y: number; h: number },
  row: number,
): GridPhaseSample | null {
  if (!box || !Number.isFinite(box.y) || !Number.isFinite(box.h)) return null;
  if (!Number.isFinite(cell.h) || cell.h <= 0) return null;
  const boxCentreAbsY = roi.y + (box.y + box.h / 2) * roi.h;
  const cellRelY = (boxCentreAbsY - cell.y) / cell.h;
  if (!Number.isFinite(cellRelY)) return null;
  return { row, cellRelY };
}

/**
 * EDGE-BASED PHASE (2026-09-08). The number-box estimate above cannot tell a
 * single-state frame's two explanations apart, and it needs number reads to
 * exist at all, which a half-cell-off lattice denies it. The card outlines
 * do not have that problem: every dex theme draws its cards on a flatter
 * page with a gutter between rows, so the per-row luminance-gradient profile
 * (dexAnchorDetector.rowEnergyProfile) peaks at every card top and bottom,
 * at the lattice pitch, whatever the cells contain. Slide the lattice's row
 * boundaries over that profile and the offset that lands them on the peaks
 * is the phase, in cell heights, positive = the real grid sits BELOW the
 * assumed lattice. Ambiguous when no offset stands clear of its rivals (a
 * frame with no periodic card structure: not a dex grid at all).
 */
export interface EdgePhaseEstimate {
  offsetCells: number;
  ambiguous: boolean;
  /** Best comb score over the runner-up score (>= 1). */
  margin: number;
}

/** How far a boundary may sit from an energy peak and still count, in px. */
const EDGE_WINDOW_PX = 3;
/** Offsets nearer than this to the winner are the same solution, not a rival. */
const EDGE_RIVAL_SEPARATION = 0.12;
/** The winner must beat its best rival by this factor to be trusted. */
const EDGE_MIN_MARGIN = 1.25;
const EDGE_STEP = 0.01;

export function estimateGridPhaseFromEdges(
  energy: ArrayLike<number>,
  frameHeight: number,
  rows: ReadonlyArray<{ top: number; bottom: number }>,
  rowHeightFrac: number,
): EdgePhaseEstimate {
  const none: EdgePhaseEstimate = { offsetCells: 0, ambiguous: true, margin: 1 };
  if (rows.length === 0 || frameHeight < 40 || !(rowHeightFrac > 0)) return none;
  const boundaries: number[] = [];
  for (const r of rows) {
    boundaries.push(r.top);
    boundaries.push(r.bottom);
  }
  const scoreAt = (offset: number): number => {
    let score = 0;
    for (const b of boundaries) {
      const yc = Math.round((b + offset * rowHeightFrac) * frameHeight);
      let best = 0;
      for (let y = yc - EDGE_WINDOW_PX; y <= yc + EDGE_WINDOW_PX; y++) {
        if (y < 1 || y >= frameHeight) continue;
        const e = energy[y];
        if (e > best) best = e;
      }
      score += best;
    }
    return score;
  };
  const candidates: Array<{ offset: number; score: number }> = [];
  const steps = Math.round(1 / EDGE_STEP);
  for (let i = 0; i < steps; i++) {
    const offset = -0.5 + i * EDGE_STEP;
    candidates.push({ offset, score: scoreAt(offset) });
  }
  let best = candidates[0];
  for (const c of candidates) {
    if (c.score > best.score + 1e-9 || (Math.abs(c.score - best.score) <= 1e-9 && Math.abs(c.offset) < Math.abs(best.offset))) best = c;
  }
  let rival = 0;
  for (const c of candidates) {
    // The comb is periodic in one cell, so an offset a whole cell away is the
    // same solution as well as one within the separation.
    const d = Math.abs(c.offset - best.offset);
    if (d < EDGE_RIVAL_SEPARATION || d > 1 - EDGE_RIVAL_SEPARATION) continue;
    rival = Math.max(rival, c.score);
  }
  if (best.score <= 0) return none;
  const margin = rival > 0 ? best.score / rival : Number.POSITIVE_INFINITY;
  const ambiguous = margin < EDGE_MIN_MARGIN;
  return { offsetCells: ambiguous ? 0 : Number(best.offset.toFixed(3)), ambiguous, margin: Number(Math.min(99, margin).toFixed(3)) };
}
